import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";


interface KnowledgeGapRow {
  Year: number;
  KnowledgeGaps: string;
}

interface ReportData {
  error: any;
  fname:                    string;
  lname:                    string;
  country:                  string;
  gradeText:                string; 
  bodyClass:                string;     
  studyYear:                number;
  completionDate:           string;
  summaryText:              string;
  recommendationText:       string;
  gradeAchieved:            number;
  hidYearScores:            string;
  hidYears:                 string;
  hidCurrentGrade:          string;
  hidCurrentGradeLevel:     string;
  hidCurrentGradeLevelStatus: string;
  hidStartIndex:            string;
  hidEndIndex:              string;
  hasFutureYears:           boolean;
  doNotShowFutureYears:     boolean;
  knowledgeGaps:            KnowledgeGapRow[];
  getStartedUrl:            string;
  printReportUrl:           string;
}

type LoadState = "idle" | "loading" | "success" | "error";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const REPORT_ENDPOINT = `${API_BASE_URL}api/reports/moving/`;

function parseQS() {
  const qs = new URLSearchParams(window.location.search);
  return {
    ReportNo:    qs.get("ReportNo")    ?? "",
    SchoolCode:  qs.get("SchoolCode")  ?? "",
    FamilyCode:  qs.get("FamilyCode")  ?? "",
    Login:       qs.get("Login")       ?? "",
  };
}

function buildApiUrl(): string {
  const p  = parseQS();
  const qs = new URLSearchParams();
  if (p.ReportNo)   qs.set("ReportNo",   p.ReportNo);
  if (p.SchoolCode) qs.set("SchoolCode", p.SchoolCode);
  if (p.FamilyCode) qs.set("FamilyCode", p.FamilyCode);
  if (p.Login)      qs.set("Login",      p.Login);
  return `${REPORT_ENDPOINT}?${qs.toString()}`;
}

function isUS(country: string): boolean {
  return country === "USA" || country === "Canada";
}

interface GradeRow {
  offset: number;   // 2,1,0,-1,-2,-3
  grade:  number;
  percent: number;
}

function buildGradeRows(
  hidYears: string,
  hidYearScores: string,
  studyYear: number,
): GradeRow[] {
  const years  = hidYears.split(",").map(Number);
  const scores = hidYearScores.split(",").map(Number);
  const scoreMap: Record<number, number> = {};
  years.forEach((y, i) => { scoreMap[y] = Math.round(scores[i] ?? 0); });

  // The 6 visible rows are offsets +2 → -3
  return [2, 1, 0, -1, -2, -3].map((offset) => ({
    offset,
    grade:   studyYear + offset,
    percent: scoreMap[studyYear + offset] ?? 0,
  }));
}

const MovingReport: React.FC = () => {
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [errorMsg,  setErrorMsg]  = useState<string>("");
  const [data,      setData]      = useState<ReportData | null>(null);

  const fetchData = useCallback(async () => {
    setLoadState("loading");
    setErrorMsg("");

    try {
      const resp = await fetch(buildApiUrl(), {
        method:      "GET",
        credentials: "include",
        headers:     { Accept: "application/json" },
      });

      if (resp.status === 204) {
        setLoadState("error");
        setErrorMsg("No report data found for this student.");
        return;
      }
      if (!resp.ok) {
        const txt = await resp.text().catch(() => resp.statusText);
        throw new Error(`HTTP ${resp.status}: ${txt}`);
      }
      const json = await resp.json() as ReportData;
      if (json.error) throw new Error(json.error);
      setData(json);
      setLoadState("success");
    } catch (err: unknown) {
      setLoadState("error");
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePrintReport = useCallback(() => {
    if (!data) return;
    // VB: Response.Redirect("~/MathsDoctor/Reports/PDFReport.aspx" & QueryString)
    window.location.href = data.printReportUrl;
  }, [data]);

  useEffect(() => {
    if (data) {
      document.body.className = data.bodyClass;
    }
    return () => { document.body.className = ""; };
  }, [data]);

  if (loadState === "loading" || loadState === "idle") {
    return (
      <div id="page_wrap">
        <div className="speech corner">
          <span id="speech_text">Loading...</span>
        </div>
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div id="page_wrap" style={{ padding: 40 }}>
        <h2 style={{ color: "#c0392b" }}>Failed to load report</h2>
        <p>{errorMsg}</p>
        <button onClick={fetchData} style={retryBtnStyle}>Retry</button>
      </div>
    );
  }

  const d      = data!;
  const au     = !isUS(d.country);
  const grade_rows = buildGradeRows(d.hidYears, d.hidYearScores, d.studyYear);

  const gradeAchievedFloat = d.gradeAchieved;

  return (
    <div id="page_wrap">

      <div id="header_bar">
        <div id="top_logo_box_left"></div>
        <div id="top_logo_box"></div>
        <div id="top_text_div">

          {/* spanFName / spanLName → runat="server" InnerText */}
          <div id="top_name">
            <p>
              <span id="spanFName" className="firstname">{d.fname}</span>
              <span id="spanLName" className="lastname">{d.lname}</span>
            </p>
          </div>

          <div id="top_title">
            <p>Knowledge of Curriculum Report</p>
          </div>

          {/* Desktop: single line — spanGradeText + spanGrade + spanCompletionDate */}
          <div className="top_details">
            <p>
              School Level:{" "}
              {/* spanGradeText: class="ctry au" or "ctry us", text="Year"/"Grade" */}
              <span
                id="spanGradeText"
                className={au ? "ctry au" : "ctry us"}
              >
                {d.gradeText}
              </span>{" "}
              {/* spanGrade */}
              <span id="spanGrade">{d.studyYear}</span>
              {" "}/ Assessment Completed:{" "}
              {/* spanCompletionDate */}
              <span id="spanCompletionDate">{d.completionDate}</span>
            </p>
          </div>

          {/* Mobile line 1: spanGradeText1 + spanGrade1 */}
          <div className="top_details2">
            <p>
              School Level:{" "}
              <span
                id="spanGradeText1"
                className={au ? "ctry au" : "ctry us"}
              >
                {d.gradeText}
              </span>{" "}
              <span id="spanGrade1">{d.studyYear}</span>
            </p>
          </div>

          {/* Mobile line 2: spanCompletionDate1 */}
          <div className="top_details2">
            <p>
              Assessment Completed:{" "}
              <span id="spanCompletionDate1">{d.completionDate}</span>
            </p>
          </div>

        </div>
      </div>

      {/* Speech bubble */}
      <div className="speech corner">
        <span id="speech_text">Loading...</span>
      </div>

      <div id="report_container">

        {/* Tutor div + play button */}
        <div id="tutor_div">
          <div className="play_btn">
            <span className="arrow_right"></span>
          </div>
        </div>

        <hr id="hr_div" />

        <div id="wall_container">

          {/* Summary + Recommendation */}
          <div className="summary">
            <p>Summary</p>
            <div>
              {/* spanSummaryText runat="server" InnerText */}
              <span id="spanSummaryText">{d.summaryText}</span>
            </div>
            <div>
              <span className="recommendation-title">Recommendation: &nbsp;</span>
              {/* spanRecommendationText runat="server" InnerText */}
              <span id="spanRecommendationText">{d.recommendationText}</span>
            </div>
          </div>

          <div className="wall-heading">Result</div>

          <div className="grades">
            {grade_rows.map(({ offset, grade, percent }) => {
              const rowId = `whitetext${offset}`;
              const cls   = offset === 0 ? "grade_current_text" : undefined;
              const suffix = offset.toString().replace("-", "-"); // e.g. "2","1","0","-1"
              return (
                <div className="graph_row" key={offset}>
                  {/* whiteout overlay — id used by MovingReport.js */}
                  <div id={rowId} className="whiteout_row"></div>

                  {/* Desktop grade box */}
                  <div className="grade_box">
                    <p className={cls}>
                      {/* .ctry.au shows "Year", .ctry.us shows "Grade" */}
                      <span className="ctry au">Year</span>
                      <span className="ctry us">Grade</span>
                      {" "}
                      <span className={`grade${suffix}`}>{grade > 0 ? grade : ""}</span>
                      (<span className={`percent${suffix}`}>{percent}</span>%)
                    </p>
                  </div>

                  {/* Mobile grade box */}
                  <div className="grade_box2">
                    <p className={cls}>
                      <span className="ctry au">Y</span>
                      <span className="ctry us">G</span>
                      <span className={`grade${suffix}`}>{grade > 0 ? grade : ""}</span>
                      {" "}(<span className={`percent${suffix}`}>{percent}</span>%)
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Graph div — populated by MovingReport.js */}
          <div id="graph"></div>

          <div className="extras">

            {/* Brick wall legend */}
            <div className="legend-container">
              <div className="legend">
                <div className="legend-heading">Brick wall legend:</div>
                <div className="legend-div">
                  <div className="yellow-icon"></div>
                  <div className="legend-text">Solid understanding</div>
                </div>
                <div className="legend-div">
                  <div className="red-icon"></div>
                  <div className="legend-text">Knowledge gaps</div>
                </div>
                <div className="brick-note">
                  &#42; Note that where the bricks are faded, this represents{" "}
                  <strong>
                    future{" "}
                    <span className="ctry au">years</span>
                    <span className="ctry us">grades</span>
                  </strong>.
                </div>
              </div>
            </div>

            {/* Grade levels achieved progress bar */}
            <div className="achieve-container">
              <div className="grade-achieved">
                <span className="ctry au">Year</span>
                <span className="ctry us">Grade</span>
                {" "}Levels Achieved
              </div>

              <div className="bar-container">
                <div className="progressbar">
                  {/* Grey mask overlay */}
                  <div className="grey-block" id="greymask"></div>

                  {/* Block 1 (special: box_wrapper_front) */}
                  <div
                    className={`green-block box_wrapper_front${gradeAchievedFloat >= 1 ? " achieved" : ""}`}
                    id="block1"
                  >
                    <div className="top_bar_num_first">0</div>
                    <div className="top_bar_num">1</div>
                  </div>

                  {/* Blocks 2-9 */}
                  {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <div
                      key={n}
                      className={`green-block${gradeAchievedFloat >= n ? " achieved" : ""}`}
                      id={`block${n}`}
                    >
                      <div className="top_bar_num">{n}</div>
                    </div>
                  ))}

                  {/* Block 10 (special: box_wrapper_end) */}
                  <div
                    className={`green-block box_wrapper_end${gradeAchievedFloat >= 10 ? " achieved" : ""}`}
                    id="block10"
                  >
                    <div className="top_bar_num">10</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Get Started button — linkGetStarted HyperLink */}
            <div id="get_started_div">
  
              {d.getStartedUrl ? (
                <a href={d.getStartedUrl} id="linkGetStarted">
                  <div id="get_started_btn">
                    <div id="plus"></div>
                    <div id="plus_text"><p>GET STARTED</p></div>
                  </div>
                </a>
              ) : (
                <div id="get_started_btn">
                  <div id="plus"></div>
                  <div id="plus_text"><p>GET STARTED</p></div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      <div id="gaps_container">

        {/* linkbtnPrintReport — asp:LinkButton, Click → linkbtnPrintReport_Click */}
        <div
          id="print_btn"
          onClick={handlePrintReport}
          style={{ cursor: "pointer" }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handlePrintReport()}
        >
          <p>PRINT REPORT</p>
        </div>

        {/* gaps_title */}
        <p id="gaps_title">
          <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span>
          {" "}Doctor<sup>&reg;</sup> has diagnosed the following gaps in{" "}
          {/* spanFname1 runat="server" InnerText */}
          <span className="firstname" id="spanFname1">{d.fname}</span>'s wall of knowledge:
        </p>

        {/* rptKnowledgeGaps Repeater */}
        <div id="gaps_box_container">
          {d.knowledgeGaps.map((gap, idx) => (
            <div className="gaps_box" key={idx}>
              <div className="gaps_subtitle">
                <p>
                  {/* <%# Eval("Year") %> → gap.Year */}
                  <span className="ctry au">Year</span>
                  <span className="ctry us">Grade</span>
                  {" "}
                  <span className="gap_grade">{gap.Year}</span>
                  &nbsp;gaps
                </p>
              </div>
              {/* <%# Eval("KnowledgeGaps") %> → gap.KnowledgeGaps */}
              <p className="gaps_listing">{gap.KnowledgeGaps}</p>
            </div>
          ))}
        </div>

      </div>{/* #gaps_container */}

      <div id="footer_div">
        <p>
          Disclaimer:{" "}
          <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span>
          {" "}Doctor&reg; reports are to be used as a guideline only. No responsibility
          is taken if results provided by{" "}
          <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span>
          {" "}Doctor&reg; do not translate to school reports or any other forms of assessment. It is assumed
          that the user used{" "}
          <span className="ctry au">Maths</span>
          <span className="ctry us">Math</span>
          {" "}Doctor&reg; in accordance with the guidelines specified.
        </p>
      </div>

      <input type="hidden" id="hidGradeAchieved"           value={String(d.gradeAchieved)}              readOnly />
      <input type="hidden" id="hidYearScores"              value={d.hidYearScores}                      readOnly />
      <input type="hidden" id="hidYears"                   value={d.hidYears}                           readOnly />
      <input type="hidden" id="hidCurrentGrade"            value={d.hidCurrentGrade}                    readOnly />
      <input type="hidden" id="hidCurrentGradeLevel"       value={d.hidCurrentGradeLevel}               readOnly />
      <input type="hidden" id="hidCurrentGradeLevelStatus" value={d.hidCurrentGradeLevelStatus}         readOnly />
      <input type="hidden" id="hidStartIndex"              value={d.hidStartIndex}                      readOnly />
      <input type="hidden" id="hidEndIndex"                value={d.hidEndIndex}                        readOnly />

    </div> /* #page_wrap */
  );
};

const retryBtnStyle: React.CSSProperties = {
  marginTop:       16,
  padding:         "10px 24px",
  fontSize:        14,
  fontWeight:      600,
  background:      "#e67e22",
  color:           "#fff",
  border:          "none",
  borderRadius:    7,
  cursor:          "pointer",
};

export default MovingReport;